/**
 * 🟦 V11.3 — Recipe Replay de Lyon.
 *
 * Store in-memory de "recetas" (cadenas de patches IA exitosas) por app.
 * Una receta es una secuencia de pasos {instruction, patch, snapshot?}
 * que se puede re-aplicar a otros nodos del mismo tipo en un click.
 *
 * Persistencia: solo en memoria de la sesión del browser (no localStorage,
 * no backend). V11.4 podría persistir por usuario.
 *
 * Scope: por appId. Las recetas de la app A NO se ven en la app B.
 * Cap: 50 recetas por appId (FIFO drop si excede).
 */

const MAX_RECIPES_PER_APP = 50;
// Map<appId, Recipe[]>
const recipesByApp = new Map();
const subscribers = new Set();

function emit(appId) {
  const list = recipesByApp.get(appId) || [];
  const snapshot = { appId, recipes: list.slice() };
  for (const cb of subscribers) {
    try { cb(snapshot); } catch { /* ignore */ }
  }
}

function uuid() {
  // crypto.randomUUID si está disponible; fallback simple.
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try { return crypto.randomUUID(); } catch { /* ignore */ }
  }
  return 'rcp-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
}

/**
 * Guarda una receta. Si excede MAX_RECIPES_PER_APP, drop del más viejo.
 * Devuelve la receta guardada (con id asignado si no venía).
 */
export function addRecipe(recipe) {
  if (!recipe || !recipe.appId || !recipe.name) return null;
  const safe = {
    id: recipe.id || uuid(),
    name: String(recipe.name).slice(0, 80),
    description: String(recipe.description || '').slice(0, 240),
    createdAt: recipe.createdAt || new Date().toISOString(),
    appId: recipe.appId,
    nodeType: (recipe.nodeType || 'div').toLowerCase(),
    steps: Array.isArray(recipe.steps)
      ? recipe.steps.filter((s) => s && s.patch && Array.isArray(s.patch)).slice(0, 20)
      : [],
  };
  if (safe.steps.length === 0) return null;
  const current = recipesByApp.get(safe.appId) || [];
  const next = [...current, safe];
  if (next.length > MAX_RECIPES_PER_APP) {
    next.splice(0, next.length - MAX_RECIPES_PER_APP);
  }
  recipesByApp.set(safe.appId, next);
  emit(safe.appId);
  return safe;
}

/** Lista todas las recetas para un appId (orden de creación). */
export function getRecipes(appId) {
  if (!appId) return [];
  return (recipesByApp.get(appId) || []).slice();
}

/**
 * 🟦 V13.1 — Seed automático del Marketplace para cada usuario.
 *
 * Trae las recetas públicas de `/api/recipes/marketplace` y las agrega
 * al store local del `appId` actual con IDs estables `mkt:{id}` para que
 * sea IDEMPOTENTE (re-ejecuciones no duplican). Best-effort: si falla la
 * red o el endpoint, simplemente no se siembran y la UI sigue normal.
 *
 * Esto resuelve el pedido del usuario: que TODAS las apps de TODOS los
 * usuarios tengan las recetas curadas del marketplace pre-cargadas en su
 * biblioteca personal, sin requerirles hacer "Importar a mi app"
 * manualmente para cada una.
 */
export async function seedMarketplaceRecipes(appId, backendUrl) {
  if (!appId) return { seeded: 0 };
  try {
    const url = `${backendUrl || ''}/api/recipes/marketplace`;
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) return { seeded: 0, error: `http_${res.status}` };
    const data = await res.json();
    const list = Array.isArray(data?.recipes) ? data.recipes : [];
    const existing = recipesByApp.get(appId) || [];
    const existingIds = new Set(existing.map((r) => r.id));
    let seeded = 0;
    for (const r of list) {
      if (!r || !r.id || !Array.isArray(r.steps) || r.steps.length === 0) continue;
      const seedId = `mkt:${r.id}`;
      if (existingIds.has(seedId)) continue;
      const ok = addRecipe({
        id: seedId,
        name: r.name,
        description: r.description || '',
        createdAt: r.createdAt || new Date().toISOString(),
        appId,
        nodeType: r.nodeType || 'div',
        steps: r.steps,
      });
      if (ok) seeded += 1;
    }
    return { seeded };
  } catch {
    return { seeded: 0, error: 'fetch_failed' };
  }
}

/** Busca una receta por id (en cualquier app). */
export function getRecipeById(recipeId) {
  if (!recipeId) return null;
  for (const list of recipesByApp.values()) {
    const found = list.find((r) => r.id === recipeId);
    if (found) return { ...found, steps: found.steps.slice() };
  }
  return null;
}

/** Elimina una receta por id. Devuelve true si se eliminó. */
export function deleteRecipe(recipeId) {
  if (!recipeId) return false;
  let removed = false;
  for (const [appId, list] of recipesByApp.entries()) {
    const next = list.filter((r) => r.id !== recipeId);
    if (next.length !== list.length) {
      recipesByApp.set(appId, next);
      emit(appId);
      removed = true;
    }
  }
  return removed;
}

/** Subscribe a cambios en recetas (de cualquier app). */
export function subscribeRecipes(cb) {
  if (typeof cb !== 'function') return () => {};
  subscribers.add(cb);
  return () => subscribers.delete(cb);
}

/** Borra todas las recetas (para tests). */
export function clearAllRecipes() {
  recipesByApp.clear();
}

// ─── Compatibilidad de nodeType ─────────────────────────────────────────
// Mapa de "buckets" de nodos compatibles. Un button puede recibir una
// receta de button o de a (link/CTA); un div puede recibir cualquier
// contenedor (div/section/article); pero NO una receta de button.
const COMPATIBILITY_BUCKETS = {
  button: ['button', 'a'],
  a: ['a', 'button'],
  div: ['div', 'section', 'article', 'main', 'aside', 'header', 'footer', 'nav'],
  section: ['div', 'section', 'article', 'main'],
  article: ['div', 'section', 'article'],
  header: ['header', 'div'],
  footer: ['footer', 'div'],
  nav: ['nav', 'div'],
  h1: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  h2: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  h3: ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'],
  p: ['p', 'span'],
  span: ['span', 'p'],
  img: ['img'],
  input: ['input', 'textarea'],
  textarea: ['textarea', 'input'],
  form: ['form'],
};

/**
 * Determina si una receta de `recipeNodeType` puede aplicarse a un
 * nodo `targetNodeType`. Si el recipeNodeType no está en el mapa,
 * permitimos sólo coincidencia exacta. Imágenes y forms son estrictos.
 */
export function isNodeTypeCompatible(recipeNodeType, targetNodeType) {
  const r = (recipeNodeType || '').toLowerCase();
  const t = (targetNodeType || '').toLowerCase();
  if (!r || !t) return false;
  if (r === t) return true;
  const bucket = COMPATIBILITY_BUCKETS[r];
  if (Array.isArray(bucket)) return bucket.includes(t);
  return false;
}

/**
 * Aplica una receta a un nodo objetivo. Envía postMessage 'uib-update-node'
 * al iframe por cada step (re-aplicando el patch sobre uib_id del target).
 *
 * Devuelve un Promise con {ok, applied?, error?, finalContext?}. NO toca
 * el undo/redo del inspector. Usa `iframeRef` pasado desde el caller para
 * evitar acoplar este módulo al DOM.
 */
export async function applyRecipeToNode(recipeId, targetNodeContext, iframeContentWindow) {
  const recipe = getRecipeById(recipeId);
  if (!recipe) return { ok: false, error: 'recipe_not_found' };
  if (!targetNodeContext || !targetNodeContext.uib_id) {
    return { ok: false, error: 'no_target_node' };
  }
  const targetTag = (targetNodeContext.tag || '').toLowerCase();
  if (!isNodeTypeCompatible(recipe.nodeType, targetTag)) {
    return {
      ok: false,
      error: 'incompatible_node_type',
      message: `Esta receta es para <${recipe.nodeType}>; el nodo seleccionado es <${targetTag || 'desconocido'}>.`,
    };
  }
  if (!iframeContentWindow) return { ok: false, error: 'iframe_unavailable' };

  // Re-aplicar cada step. Como recipe.steps[i].patch es un JSON Patch
  // RFC 6902 ya validado y aplicado en el pasado, lo enviamos tal cual al
  // iframe. El iframe interpreta /text, /className, /styles/*, /attrs/*,
  // /responsive/{bp}/* y muta el DOM en consecuencia.
  let finalContext = { ...targetNodeContext };
  const appliedPatches = [];
  for (const step of recipe.steps) {
    if (!Array.isArray(step.patch) || step.patch.length === 0) continue;
    // Calcular los `changes` localmente para mantener finalContext fresco
    // (espejo de aiContextual.applyJsonPatch — duplicado mínimo para
    // evitar dependencia circular).
    const changes = applyJsonPatchLocal(finalContext, step.patch);
    if (Object.keys(changes).length === 0) continue;
    try {
      iframeContentWindow.postMessage({
        kind: 'uib-update-node',
        uib_id: targetNodeContext.uib_id,
        changes,
      }, '*');
    } catch {
      return { ok: false, error: 'postmessage_failed', appliedPatches };
    }
    finalContext = {
      ...finalContext,
      ...changes,
      styles: { ...(finalContext.styles || {}), ...(changes.styles || {}) },
      attrs: { ...(finalContext.attrs || {}), ...(changes.attrs || {}) },
      responsive: changes.responsive || finalContext.responsive,
    };
    appliedPatches.push(step.patch);
    // Pequeño delay para que el iframe procese (idempotente en realidad,
    // pero ayuda a debug y a que las CSS transitions se vean).
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 50));
  }

  return {
    ok: true,
    applied: appliedPatches.length,
    finalContext,
    instructions: recipe.steps.map((s) => s.instruction).filter(Boolean),
  };
}

/**
 * Mini-aplicador local de JSON Patch (RFC 6902) — devuelve los `changes`
 * a fusionar sobre el nodo. Soporta los mismos paths que aiContextual.js
 * (/text, /className, /styles/*, /attrs/*, /responsive/{bp}/*).
 */
function applyJsonPatchLocal(node, patch) {
  const changes = {};
  for (const op of patch || []) {
    if (!op || !op.path) continue;
    const path = op.path;
    if (path === '/text' && (op.op === 'replace' || op.op === 'add')) {
      changes.text = String(op.value ?? '');
    } else if (path === '/className' && (op.op === 'replace' || op.op === 'add')) {
      changes.className = String(op.value ?? '');
    } else if (path.startsWith('/styles/')) {
      const key = path.slice('/styles/'.length);
      if (!key) continue;
      if (!changes.styles) changes.styles = { ...(node.styles || {}) };
      if (op.op === 'remove') delete changes.styles[key];
      else changes.styles[key] = String(op.value ?? '');
    } else if (path.startsWith('/attrs/')) {
      const key = path.slice('/attrs/'.length);
      if (!key) continue;
      if (!changes.attrs) changes.attrs = { ...(node.attrs || {}) };
      if (op.op === 'remove') delete changes.attrs[key];
      else changes.attrs[key] = String(op.value ?? '');
    } else if (path.startsWith('/responsive/')) {
      const rest = path.slice('/responsive/'.length).split('/');
      const bp = rest[0];
      const key = rest[1];
      if (!bp || !key || !['lg', 'md', 'sm'].includes(bp)) continue;
      if (!changes.responsive) {
        changes.responsive = {
          lg: { ...((node.responsive && node.responsive.lg) || {}) },
          md: { ...((node.responsive && node.responsive.md) || {}) },
          sm: { ...((node.responsive && node.responsive.sm) || {}) },
        };
      }
      if (op.op === 'remove') delete changes.responsive[bp][key];
      else changes.responsive[bp][key] = String(op.value ?? '');
    }
  }
  return changes;
}
